import { journeyStore } from "@/app/services/journey";
import { PartnerType } from "@/app/utils/partners.enum";
import { zodResolver } from "@hookform/resolvers/zod";
import { differenceInYears, isValid, parse } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useCrefazClt } from "@/app/contexts/CrefazCltContext";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { cepService } from "@/app/services/cepService";
import { crefazCltService } from "@/app/services/crefazCltService";
import { validateCpf } from "@/app/utils/cpfFormater";
import { formatDateForInput } from "@/app/utils/formatDate";
import masks from "@/app/utils/masks";
import { useToast } from "@/view/components/ui/use-toast";
import { useRegisterSupportContext } from "@/app/store/supportScreenContext"

const preAnalysisSchema = z.object({
  cpf: z
    .string()
    .min(11, "CPF é obrigatório")
    .refine((v) => validateCpf(v), "CPF Inválido")
    .transform((f) => f.replace(/\D/g, "")),
  name: z.string().min(1, "Nome é obrigatório"),
  birthDate: z
    .string()
    .min(1, "Data de nascimento é obrigatória")
    .refine((value) => {
      const date = parse(value, "dd/MM/yyyy", new Date());

      return isValid(date) && differenceInYears(new Date(), date) >= 18;
    }, "O cliente precisa ter ao menos 18 anos"),
  phone: z
    .string()
    .transform((v) => v.replace(/\D/g, ""))
    .refine((v) => v.length === 11, "Telefone inválido"),
  // obrigatório: o cadastro do cliente é criado com ele
  email: z.string().min(1, "E-mail é obrigatório").email("E-mail inválido"),
  cep: z
    .string()
    .transform((v) => v.replace(/\D/g, ""))
    .refine((v) => v.length === 8, "CEP inválido"),
  logradouro: z.string().min(1, "Logradouro é obrigatório"),
  bairro: z.string().min(1, "Bairro é obrigatório"),
  cidade: z.string().min(1, "Cidade é obrigatória"),
  uf: z.string().length(2, "UF é obrigatória"),
});

type PreAnalysisFormData = z.infer<typeof preAnalysisSchema>;

interface UsePreAnalysisModalProps {
  onContinue?: () => void;
}

export default function usePreAnalysisModal({
  onContinue,
}: UsePreAnalysisModalProps) {
  const form = useForm<PreAnalysisFormData>({
    resolver: zodResolver(preAnalysisSchema),
    mode: "onChange",
  });

  const { watch, setValue, handleSubmit } = form;
  const { toast } = useToast();
  const { setCustomerData, setPreTransaction } = useCrefazClt();

  const [loading, setLoading] = useState(false);

  const cpf = watch("cpf")?.replace(/\D/g, "");
  const cep = watch("cep")?.replace(/\D/g, "");

  const {
    data: customer,
    isSuccess,
    isFetching: isFetchingCustomer,
  } = useCustomerCpf(cpf);

  useEffect(() => {
    if (!isSuccess || !customer) return;

    const setField = (field: keyof PreAnalysisFormData, value?: string) => {
      if (value) setValue(field, value, { shouldValidate: true });
    };

    setField("name", customer.name);
    setField("email", customer.email);
    setField("phone", masks.phone(customer.phone ?? ""));
    setField(
      "birthDate",
      customer.birthDate ? formatDateForInput(customer.birthDate) : undefined,
    );
    setField("cep", masks.zipcode(customer.address?.zipCode ?? ""));
    setField("bairro", customer.address?.neighborhood);
    setField("cidade", customer.address?.city);
    setField("uf", customer.address?.state);
    setField("logradouro", customer.address?.street);
  }, [isSuccess, customer, setValue]);

  useEffect(() => {
    if (cep?.length !== 8) return;

    const fetchAddress = async () => {
      try {
        const address = await cepService(cep);

        if (address?.erro) return;

        setValue("logradouro", address.logradouro, { shouldValidate: true });
        setValue("bairro", address.bairro, { shouldValidate: true });
        setValue("cidade", address.localidade, { shouldValidate: true });
        setValue("uf", address.uf, { shouldValidate: true });
      } catch {
        toast({
          title: "Não foi possível buscar o CEP",
          description: "Preencha o endereço manualmente.",
          variant: "destructive",
        });
      }
    };

    fetchAddress();
  }, [cep, setValue, toast]);

  const submitModal = handleSubmit(async (formData: PreAnalysisFormData) => {
    try {
      setLoading(true);
      journeyStore.start(PartnerType.CREFAZ_CLT);

      const birthDate = formData.birthDate.split("/").reverse().join("-");

      const response = await crefazCltService.postPreAnalysis({
        name: formData.name,
        cpf: formData.cpf,
        birthDate,
        phone: formData.phone,
        email: formData.email,
        endereco: {
          logradouro: formData.logradouro,
          bairro: formData.bairro,
          cep: formData.cep,
          cidade: formData.cidade,
          uf: formData.uf,
        },
      });

      setCustomerData({
        cpf: formData.cpf,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        birthDate,
        address: {
          zipCode: formData.cep,
          street: formData.logradouro,
          neighborhood: formData.bairro,
          city: formData.cidade,
          state: formData.uf,
        },
      });

      setPreTransaction({
        id: response.id,
        status: response.status,
        processoId: response.processoId,
        propostaId: response.propostaId,
        autorizacaoEnviada: response.autorizacaoEnviada,
      });

      onContinue?.();
    } catch (error: any) {
      toast({
        title: "Não foi possível iniciar a pré-análise",
        description:
          error?.message || "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  });

  useRegisterSupportContext("simulation.crefazClt.preAnalysis", {
    product: PartnerType.CREFAZ_CLT,
    step: "Pré-análise Crefaz CLT",
    data: form.watch(),
  });

  return {
    form,
    submitModal,
    loading,
    isFetchingCustomer,
  };
}
